<template>
  <b-card no-body>
    <b-overlay :show="loading" rounded="sm" variant="light">
      <b-card-header>
        <div>
          <b-card-title class="mb-1">
            {{ $t("dashboard.incomingConversationAndCall") }}
          </b-card-title>
        </div>

        <b-button-group class="mt-1 mt-sm-0">
          <b-button
            v-for="option in filterOptions"
            :key="option"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            :variant="option == query.date ? 'primary' : 'outline-primary'"
            size="sm"
            @click="changeDate(option)"
          >
            {{ $t(`period.${option}`) }}
          </b-button>
        </b-button-group>
      </b-card-header>

      <b-card-body>
        <chartjs-component-line-chart
          :key="`comingConversatoionChart${key}`"
          :height="400"
          :data="chartjsData.lineChart.data"
          :options="chartjsDataOptions"
          :plugins="plugins"
        />
      </b-card-body>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BCardSubTitle,
  BCardTitle,
  BButtonGroup,
  BButton,
  BOverlay,
} from "bootstrap-vue";
import ChartjsComponentLineChart from "@/components/charts-components/ChartjsComponentLineChart.vue";
import Ripple from "vue-ripple-directive";
import Repository from "@/repositories/RepositoryFactory";
import moment from "moment-timezone";
import useJwt from "@/auth/jwt/useJwt";
import chartjsData, { defaultDataset, chartColors } from "./chartjsData";

const DashboardRepository = Repository.get("dashboard");

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BCardSubTitle,
    BCardTitle,
    BButtonGroup,
    BButton,
    BOverlay,
    ChartjsComponentLineChart,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      chartjsData,
      chartjsDataOptions: {
        ...chartjsData.lineChart.options,
        legend: {
          display: true,
          position: "bottom",
        },
      },
      chartjsDataLineChart: {
        // labels: chartjsData.,
      },
      filterOptions: ["1H", "1D", "1W", "1M", "1Y"],
      query: {
        date: this.$route.query.date || "1W",
      },
      plugins: [
        // to add spacing between legends and chart
        {
          beforeInit(chart) {
            /* eslint-disable func-names, no-param-reassign */
            chart.legend.afterFit = function () {
              this.height += 20;
            };
            /* eslint-enable */
          },
        },
      ],
      chatChartData: {},
      callChartData: {},
      key: 1,
      loading: true,
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },
    userData() {
      return this.$store.state.profile.email || this.$store.state.profile.phone  
        ? this.$store.state.profile
        : JSON.parse(
            localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName)
          );
    },
  },
  watch: {
    currentLocale(value, oldValue) {
      if (
        oldValue &&
        value &&
        (this.chatChartData?.data?.length || this.callChartData?.data?.length)
      ) {
        this.mapChartData({
          data: this.chatChartData,
          data1: this.callChartData,
          label: this.$t("dashboard.incomingConversation"),
          label1: this.$t("dashboard.incomingCall"),
        });
      }
    },
    "query.date": function () {
      this.index();
    },
  },
  created() {},
  mounted() {
    this.index();
  },
  methods: {
    changeDate(date) {
      this.query.date = date;
      this.updateQuerySting();
    },
    updateQuerySting() {
      this.$router.replace({ query: this.query });
    },
    async index() {
      this.loading = true;

      try {
        const [chat, call] = await Promise.all([
          DashboardRepository.comingConversation({
            period: this.query.date,
          }),
          DashboardRepository.comingCall({
            period: this.query.date,
          }),
        ]);

        this.chatChartData = chat?.data?.data?.comingConversationChart;
        this.callChartData = call?.data?.data?.comingCallChart;

        if (this.chatChartData && this.callChartData) {
          this.mapChartData({
            data: this.chatChartData,
            data1: this.callChartData,
            label: this.$t("dashboard.incomingConversation"),
            label1: this.$t("dashboard.incomingCall"),
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    mapChartData({ data, label, data1, label1 }) {
      const labels = [];
      switch (this.query.date) {
        case "1H":
          for (const label of data.labels) {
            labels.push(
              moment(`${label}`)
                .tz(this.userData.origin)
                .locale(this.currentLocale)
                .format("hh:mm a")
            );
          }
          break;
        case "1D":
          for (const label of data.labels) {
            labels.push(
              moment(`${label}`)
                .tz(this.userData.origin)
                .locale(this.currentLocale)
                .format("hh:00 a")
            );
          }
          break;
        case "1W":
          for (const label of data.labels) {
            labels.push(
              moment(`${label}`)
                .tz(this.userData.origin)
                .locale(this.currentLocale)
                .format("dddd")
            );
          }
          break;
        case "1M":
          for (const label of data.labels) {
            labels.push(
              moment(`${label}`)
                .tz(this.userData.origin)
                .locale(this.currentLocale)
                .format("DD MMM")
            );
          }
          break;
        case "1Y":
          for (const label of data.labels) {
            labels.push(
              moment(`${label}`)
                .tz(this.userData.origin)
                .locale(this.currentLocale)
                .format("MMM YYYY")
            );
          }
          break;
        default:
          break;
      }

      this.chartjsData.lineChart.data.labels = [...labels];
      this.chartjsData.lineChart.data.datasets = [
        {
          ...defaultDataset,
          data: [...data.data],
          label,
        },
        {
          borderColor: "#c0392b",
          lineTension: 0.5,
          pointStyle: "circle",
          backgroundColor: "#c0392b",
          lineTension: 0.5,
          pointStyle: "circle",
          fill: false,
          pointRadius: 1,
          pointHoverRadius: 5,
          pointHoverBorderWidth: 5,
          pointBorderColor: "transparent",
          pointHoverBorderColor: "#fdfdfd",
          pointHoverBackgroundColor: chartColors.lineChartPrimary,
          pointShadowOffsetX: 1,
          pointShadowOffsetY: 1,
          pointShadowBlur: 5,
          pointShadowColor: chartColors.tooltipShadow,
          fill: false,
          data: [...data1.data],
          label: label1,
        },
      ];

      this.key++;
    },
  },
};
</script>
